import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianCurrency, formatIndianDate } from '../utils/format';

const ProjectOverview = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState({ total: 0, open: 0, inprogress: 0, completed: 0, overdue: 0 });
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const projectIdNum = useMemo(() => Number(projectId) || 0, [projectId]);

  useEffect(() => {
    const load = async () => {
      if (!projectIdNum) return;
      try {
        setLoading(true);
        const isAdminOrBdm = user?.role === 'ADMIN' || user?.role === 'BDM';
        const requests = [
          api.get(`/api/projects/${projectIdNum}`),
          api.get(`/api/projects/${projectIdNum}/summary`)
        ];
        if (isAdminOrBdm) {
          requests.push(api.get('/api/audit-log', { params: { entity_type: 'PROJECT', entity_id: projectIdNum, page: 1, limit: 20 } }));
        }
        const [pRes, sRes, aRes] = await Promise.all(requests);
        setProject(pRes.data || null);
        setSummary(sRes.data || { total: 0, open: 0, inprogress: 0, completed: 0, overdue: 0 });
        if (isAdminOrBdm && aRes) {
          const payload = aRes.data;
          setAudit(payload && typeof payload === 'object' && Array.isArray(payload.items) ? payload.items : []);
        } else {
          setAudit([]);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectIdNum, user?.role]);

  if (!projectIdNum) {
    return (
      <div className="glass-card p-6">
        <div className="text-lg font-semibold">Invalid project</div>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center p-8">Loading project...</div>;
  }

  if (!project) {
    return (
      <div className="glass-card p-6">
        <div className="text-lg font-semibold">Project not found</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="text-2xl font-bold">{project.title}</div>
          <div className="text-secondary">Client: {project.client_name || '—'}</div>
          <div className="text-xs text-tertiary mt-2">Created: {formatIndianDate(project.created_at)}</div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button type="button" className="glass-button" onClick={() => navigate('/projects')}>Back</button>
          {user.role !== 'CLIENT' ? (
            <button type="button" className="glass-button" onClick={() => navigate(`/tasks?projectId=${project.id}`)}>Tasks</button>
          ) : null}
          {user.role !== 'CLIENT' ? (
            <button type="button" className="glass-button" onClick={() => navigate(`/pmb/${project.id}`)}>PMB</button>
          ) : null}
          {user.role !== 'CLIENT' ? (
            <button type="button" className="glass-button" onClick={() => navigate(`/approvals?projectId=${project.id}`)}>Approvals</button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-6">
          <div className="text-sm font-semibold mb-2">Status</div>
          <div className="flex gap-2 flex-wrap">
            {project.status ? (
              <span className={`status-badge status-${String(project.status).toLowerCase()}`}>{project.status}</span>
            ) : null}
            {project.priority ? (
              <span className={`status-badge priority-${String(project.priority).toLowerCase()}`}>{project.priority}</span>
            ) : null}
            {project.is_active === 0 ? (
              <span className="status-badge approval-rejected">Inactive</span>
            ) : null}
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="text-sm font-semibold mb-2">Commercials</div>
          <div className="text-sm">
            <div>
              <span className="text-secondary">Budget:</span>
              <span className="ml-2 font-medium">{project.budget ? formatIndianCurrency(project.budget) : 'Not set'}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <span className="text-secondary">Quoted:</span>
              <span className="ml-2 font-medium">{project.quoted_amount ? formatIndianCurrency(project.quoted_amount) : 'Not quoted'}</span>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 col-span-2">
          <div className="text-sm font-semibold mb-2">Task Summary</div>
          <div className="grid grid-cols-5 gap-3">
            <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="text-xs text-tertiary">Total</div>
              <div className="text-xl font-semibold">{summary.total || 0}</div>
            </div>
            <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="text-xs text-tertiary">Open</div>
              <div className="text-xl font-semibold">{summary.open || 0}</div>
            </div>
            <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="text-xs text-tertiary">InProgress</div>
              <div className="text-xl font-semibold">{summary.inprogress || 0}</div>
            </div>
            <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="text-xs text-tertiary">Completed</div>
              <div className="text-xl font-semibold">{summary.completed || 0}</div>
            </div>
            <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="text-xs text-tertiary">Overdue</div>
              <div className="text-xl font-semibold">{summary.overdue || 0}</div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 col-span-2">
          <div className="text-sm font-semibold mb-2">People</div>
          <div className="project-pills">
            {project.assigned_client_user_name ? (
              <span className="name-pill pill-client">Client user: {project.assigned_client_user_name}</span>
            ) : null}
            {String(project.sales_names || '')
              .split(',')
              .map((x) => x.trim())
              .filter(Boolean)
              .map((n) => (
                <span key={`s-${n}`} className="name-pill pill-sales">Sales: {n}</span>
              ))}
            {String(project.developer_names || '')
              .split(',')
              .map((x) => x.trim())
              .filter(Boolean)
              .map((n) => (
                <span key={`d-${n}`} className="name-pill pill-dev">Dev: {n}</span>
              ))}
            {project.owner_name ? <span className="name-pill">Owner: {project.owner_name}</span> : null}
          </div>
          {project.owner_email ? (
            <div className="text-xs text-tertiary" style={{ marginTop: 8 }}>Owner email: {project.owner_email}</div>
          ) : null}
        </div>

        {project.notes ? (
          <div className="glass-card p-6 col-span-2">
            <div className="text-sm font-semibold mb-2">Notes</div>
            <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{project.notes}</div>
          </div>
        ) : null}

        {(user.role === 'ADMIN' || user.role === 'BDM') && (
          <div className="glass-card p-6 col-span-2">
            <div className="text-sm font-semibold mb-2">History</div>
            {audit.length === 0 ? (
              <div className="text-sm text-secondary">No history yet</div>
            ) : (
              <div className="grid gap-2">
                {audit.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-3" style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 12, background: 'rgba(255,255,255,0.03)' }}>
                    <div className="min-w-0">
                      <div className="font-medium">{a.action}</div>
                      <div className="text-xs text-tertiary">{formatIndianDate(a.created_at)}</div>
                    </div>
                    {a.actor_role ? (
                      <span className={`role-pill role-${String(a.actor_role).toLowerCase()}`}>{a.actor_role}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectOverview;
